import { useReducer, useMemo } from "react";
import { FilterOperator } from "../enums/filter-operator";
import { isString, isNullish } from "../utils/type-util";
import { deepAccess } from "../utils/object-util";
import { useUpdateEffect } from "./use-update-effect";
/**
 * The default operator is used when the filter descriptor has no operator.
 */
const defaultOperator = FilterOperator.Any.EQUALS.key;

/**
 * The default options for page size selection.
 */
const defaultPageSizeOptions = ["25", "50", "100", "200"];

/**
 * Maps each filter operator to a function that compares the record property and filter value.
 */
const filterExecutors = {
  [FilterOperator.Any.EQUALS.key](prop, value) {
    return prop === value;
  },
  [FilterOperator.String.CONTAINS.key](prop, value) {
    return Array.isArray(prop) ? prop.some(p => contains(p, value)) : contains(prop, value);
  },
  [FilterOperator.String.STARTS_WITH.key](prop, value) {
    return Array.isArray(prop) ? prop.some(p => startsWith(p, value)) : startsWith(prop, value);
  },
  [FilterOperator.Date.BEFORE.key](prop, value) {
    return !!prop.isBefore?.(value);
  },
  [FilterOperator.Date.AFTER.key](prop, value) {
    return !!prop.isAfter?.(value);
  },
  [FilterOperator.Date.BETWEEN.key](prop, value) {
    return Array.isArray(value) && !!prop.isAfter?.(value[0]) && !!prop.isBefore?.(value[1]);
  },
  [FilterOperator.Number.LESS_THAN.key](prop, value) {
    return prop < value;
  },
  [FilterOperator.Number.GREATER_THAN.key](prop, value) {
    return prop > value;
  },
  [FilterOperator.Array.INCLUDES.key](prop, value) {
    const props = Array.isArray(prop) ? prop : [prop];
    const values = Array.isArray(value) ? value : [value];
    return values.length === 0 || props.some(p => values.includes(p));
  },
  [FilterOperator.Array.EXCLUDES.key](prop, value) {
    const props = Array.isArray(prop) ? prop : [prop];
    const values = Array.isArray(value) ? value : [value];
    return values.length === 0 || !props.some(p => values.includes(p));
  }
};

/**
 * Uses query state for table components with client-side paging, sorting, and filtering.
 */
export function useTableQuery() {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    data,
    pageSizeOptions = defaultPageSizeOptions,
    defaultPageSize,
    defaultSorter
  } = props;
  const initialState = {
    query: {
      pagination: {
        current: 1,
        pageSize: Number(defaultPageSize ?? pageSizeOptions[0]),
        hideOnSinglePage: Number(pageSizeOptions[0]) >= (data?.length ?? 0),
        showSizeChanger: true,
        pageSizeOptions: pageSizeOptions,
        showTotal: (total, range) => {
          return `${range[0]} - ${range[1]} of ${total} items`;
        }
      },
      filters: {},
      sorter: defaultSorter ?? {}
    },
    selectedRowKeys: []
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const result = useMemo(() => applyQuery(data, state.query), [data, state.query]);
  useUpdateEffect(() => {
    dispatch({
      query: {
        pagination: {
          current: 1,
          hideOnSinglePage: Number(pageSizeOptions[0]) >= result.total
        }
      }
    });
  }, [result.total]);
  const setQuery = (pagination, filters, sorter) => {
    dispatch({
      query: {
        pagination,
        filters,
        sorter
      }
    });
  };
  const setPagination = pagination => {
    dispatch({
      query: {
        pagination
      }
    });
  };
  const setFilters = filters => {
    dispatch({
      query: {
        filters
      }
    });
  };
  const setSorter = sorter => {
    dispatch({
      query: {
        sorter
      }
    });
  };
  const setSelectedRowKeys = selectedRowKeys => {
    dispatch({
      selectedRowKeys
    });
  };
  const clearSelectedRowKeys = () => {
    dispatch({
      selectedRowKeys: []
    });
  };
  return {
    ...state,
    ...result,
    setQuery,
    setPagination,
    setFilters,
    setSorter,
    setSelectedRowKeys,
    clearSelectedRowKeys
  };
}

/**
 * Applies a query to a given list of records.
 */
function applyQuery(data, query) {
  if (!data) {
    return {
      data: [],
      total: 0
    };
  }
  const {
    pagination,
    sorter,
    filters
  } = query;
  const filteredData = filterRecords(data, filters);
  if (sorter.field && sorter.order === "ascend") {
    filteredData.sort(compareRecords(sorter.field));
  }
  if (sorter.field && sorter.order === "descend") {
    filteredData.sort(negate(compareRecords(sorter.field)));
  }
  const begin = (pagination.current - 1) * pagination.pageSize;
  const end = begin + pagination.pageSize;
  return {
    data: filteredData.slice(begin, end),
    total: filteredData.length
  };
}

/**
 * Filters a list of records using the given query.
 */
function filterRecords(data, filters) {
  if (Array.isArray(filters)) {
    filters = filters.reduce((obj, desc) => {
      obj[desc.dataIndex] = [desc];
      return obj;
    }, {});
  }
  const filterEntries = Object.entries(filters);
  return data.filter(record => {
    return filterEntries.every(_ref => {
      let [field, descriptors] = _ref;
      if (descriptors && descriptors.length > 0) {
        const prop = deepAccess(record, field);
        return descriptors.some(desc => matchesDescriptor(prop, desc));
      }
      return true;
    });
  });
}

/**
 * Determines whether a record property matches a given filter descriptor.
 */
function matchesDescriptor(prop, descriptor) {
  const value = isString(descriptor) ? descriptor : descriptor.value;
  const operator = isString(descriptor) ? defaultOperator : descriptor.operator;
  return filterExecutors[operator]?.(prop, value) ?? true;
}

/**
 * Compares two records for sorting.
 */
function compareRecords(field) {
  return (a, b) => {
    const propOne = deepAccess(a, field);
    const propTwo = deepAccess(b, field);
    if (isString(propOne) || isString(propTwo)) {
      const s1 = isNullish(propOne) ? "" : String(propOne);
      const s2 = isNullish(propTwo) ? "" : String(propTwo);
      return s1.localeCompare(s2);
    }
    if (isDate(propOne) && isDate(propTwo)) {
      const d1 = propOne.isValid() ? propOne.unix() : 0;
      const d2 = propTwo.isValid() ? propTwo.unix() : 0;
      return d1 - d2;
    }
    const n1 = propOne ?? 0;
    const n2 = propTwo ?? 0;
    return n1 - n2;
  };
}

/**
 * Checks whether a value is a date object. (moment or dayjs)
 */
function isDate(val) {
  return !!val?.isValid && !!val?.unix;
}

/**
 * Checks whether a string contains a value.
 */
function contains(str, val) {
  return String(str).trim().toLowerCase().includes(String(val).trim().toLowerCase());
}

/**
 * Checks whether a string starts with a value.
 */
function startsWith(str, val) {
  return String(str).trim().toLowerCase().startsWith(String(val).trim().toLowerCase());
}

/**
 * Creates a function that negates the numeric result of a given function.
 */
function negate(func) {
  return function () {
    return -func(...arguments);
  };
}

/**
 * Reducer for the data source state.
 */
function reducer(state, action) {
  return {
    ...state,
    ...action,
    query: {
      ...state.query,
      ...action.query,
      pagination: {
        ...deepAccess(state, "query.pagination", {}),
        ...deepAccess(action, "query.pagination", {})
      }
    }
  };
}